"use client"

import React, { useState, useEffect } from "react"
//import NewsFeeds from "./Layout/NewsFeed"
import "../index.css"
import { useLocation } from "react-router-dom"
import { connect } from "react-redux"
import { setDarkMode } from "../redux/actions/themeActions"
import FeatureBar from "./FeatureBar"
import MobileBottomNav from "./MobileBottomNav"
import { useTranslation } from "../i18n"
import { useAuth0 } from "@auth0/auth0-react"
import BugReportButton from "./BugReportButton"

export const isIosDevice = (navigatorLike) => {
  if (!navigatorLike) return false

  return (
    /iPad|iPhone|iPod/i.test(navigatorLike.userAgent || "") ||
    (navigatorLike.platform === "MacIntel" && navigatorLike.maxTouchPoints > 1)
  )
}

export const getMobileInstallPromptContent = (isIos) =>
  isIos
      ? {
        title: "Put TYK on your iPhone Home Screen.",
        text: "Apple installs web apps through Safari. It only takes a few taps.",
        action: "Show me how",
      }
    : {
        title: "Download the TYK app for a better experience.",
        text: "Use the download button in your browser address bar, or tap below to install.",
        action: "Download the app",
      }

export const shouldShowPlatformNavigation = (isAuthenticated, pathname) =>
  isAuthenticated || (pathname !== '/' && pathname !== '/public')

function Layout({ children, isDarkMode, setDarkMode }) {
  // Keep local state for backward compatibility
  const [localIsDarkMode, setLocalIsDarkMode] = useState(true)
  const [isMobile, setIsMobile] = useState(false)
  const [showMobilePrompt, setShowMobilePrompt] = useState(false)
  const [installPromptEvent, setInstallPromptEvent] = useState(null)
  const [isAppInstalled, setIsAppInstalled] = useState(false)
  const [showIosInstallHelp, setShowIosInstallHelp] = useState(false)
  const [isIos] = useState(() =>
    typeof window !== "undefined" && isIosDevice(window.navigator)
  )
  const { t } = useTranslation()
  const { isAuthenticated } = useAuth0()
  const currentYear = new Date().getFullYear()
  const location = useLocation()
  const installPromptContent = getMobileInstallPromptContent(isIos)
  const isSetupProfile =
    location.pathname === '/setup-profile' || location.pathname === '/profile-setup'
  const showPlatformNavigation = shouldShowPlatformNavigation(isAuthenticated, location.pathname)
  const isLoggedOutLanding = !showPlatformNavigation

  console.log("Redux isDarkMode:", isDarkMode)

  // Initialize Redux state from local state on first render
  useEffect(() => {
    setDarkMode(localIsDarkMode)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on component mount

  // Apply dark mode class based on Redux state
  useEffect(() => {
    console.log("Effect: Applying dark mode class", isDarkMode)
    if (isDarkMode) {
      document.documentElement.classList.add("dark")
    } else {
      document.documentElement.classList.remove("dark")
    }
    // Update local state to stay in sync with Redux
    setLocalIsDarkMode(isDarkMode)
  }, [isDarkMode])

  useEffect(() => {
    if (typeof window === "undefined") return
    const mediaQuery = window.matchMedia("(max-width: 768px)")
    const updateIsMobile = (event) => {
      setIsMobile(event.matches)
    }

    setIsMobile(mediaQuery.matches)

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateIsMobile)
    } else {
      mediaQuery.addListener(updateIsMobile)
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", updateIsMobile)
      } else {
        mediaQuery.removeListener(updateIsMobile)
      }
    }
  }, [])

  useEffect(() => {
    if (typeof window === "undefined") return
    setShowMobilePrompt(isMobile && !isAppInstalled)
  }, [isMobile, isAppInstalled])

  useEffect(() => {
    if (typeof window === "undefined") return
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true
    setIsAppInstalled(isStandalone)
  }, [])

  useEffect(() => {
    if (typeof window === "undefined") return
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault()
      setInstallPromptEvent(event)
    }

    const handleAppInstalled = () => {
      setIsAppInstalled(true)
      setShowMobilePrompt(false)
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt)
    window.addEventListener("appinstalled", handleAppInstalled)

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt)
      window.removeEventListener("appinstalled", handleAppInstalled)
    }
  }, [])

  const handleDismissMobilePrompt = () => {
    setShowMobilePrompt(false)
    setShowIosInstallHelp(false)
  }

  const handleInstallClick = async () => {
    if (!installPromptEvent) return
    installPromptEvent.prompt()
    await installPromptEvent.userChoice
    setInstallPromptEvent(null)
  }

  // Pass the Redux state to children and filter out Chat on setup page
  const childrenArray = React.Children.toArray(children).filter((child) => {
    if (isSetupProfile && React.isValidElement(child) && child.type && child.type.name === 'Chat') {
      return false
    }
    return true
  })

  const childrenWithProps = childrenArray.map((child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { isDarkMode })
    }
    return child
  })

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <div className="flex flex-grow">
        <div className="flex-grow flex flex-col">
          {isMobile && showMobilePrompt && !isAppInstalled && (
            <div className="mobile-download-prompt" role="region" aria-label="Install TYK" aria-live="polite">
              <div className="mobile-download-prompt__content">
                <p className="mobile-download-prompt__title">{installPromptContent.title}</p>
                <p className="mobile-download-prompt__text">{installPromptContent.text}</p>
              </div>
              <div className="mobile-download-prompt__actions">
                <button
                  type="button"
                  className="mobile-download-prompt__install"
                  onClick={isIos ? () => setShowIosInstallHelp(true) : handleInstallClick}
                  disabled={!isIos && !installPromptEvent}
                >
                  {installPromptContent.action}
                </button>
                <button
                  type="button"
                  className="mobile-download-prompt__close"
                  onClick={handleDismissMobilePrompt}
                  aria-label="Dismiss download prompt"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          {showIosInstallHelp && (
            <div className="ios-install-help__backdrop">
              <section
                className="ios-install-help"
                role="dialog"
                aria-modal="true"
                aria-labelledby="ios-install-help-title"
              >
                <button
                  type="button"
                  className="ios-install-help__close"
                  onClick={() => setShowIosInstallHelp(false)}
                  aria-label="Close iPhone installation instructions"
                >
                  ✕
                </button>
                <img src="/apple-touch-icon.png" alt="TYK crown app icon" className="ios-install-help__icon" />
                <p className="ios-install-help__eyebrow">INSTALL ON IPHONE</p>
                <h2 id="ios-install-help-title">Add TYK to your Home Screen</h2>
                <p className="ios-install-help__intro">Open TYK in Safari, then follow these steps:</p>
                <ol className="ios-install-help__steps">
                  <li><strong>Tap Share</strong><span>Use the square icon with the upward arrow.</span></li>
                  <li><strong>Tap Add to Home Screen</strong><span>Scroll down if it is not immediately visible.</span></li>
                  <li><strong>Turn on Open as Web App</strong><span>This gives TYK its full-screen app experience.</span></li>
                  <li><strong>Tap Add</strong><span>The TYK crown will appear with your other apps.</span></li>
                </ol>
                <button
                  type="button"
                  className="ios-install-help__done"
                  onClick={() => setShowIosInstallHelp(false)}
                >
                  Got it
                </button>
              </section>
            </div>
          )}

          {!isLoggedOutLanding && <FeatureBar />}

          <main className="flex-grow flex flex-col">{childrenWithProps}</main>
          {!isSetupProfile && <BugReportButton />}
          {!isSetupProfile && !isLoggedOutLanding && <MobileBottomNav />}

          {!isSetupProfile && (
            <footer className="bg-primary text-primary-foreground py-4">
              <div className="container mx-auto px-4 text-center">
                <p>{t("layout.footer", { year: currentYear })}</p>
              </div>
            </footer>
          )}
        </div>
      </div>
    </div>
  )
}

// Map Redux state to component props
const mapStateToProps = (state) => {
  console.log("mapStateToProps called, state:", state)
  return {
    isDarkMode: state.theme.isDarkMode,
  }
}

// Map Redux actions to component props
const mapDispatchToProps = {
  setDarkMode,
}

// Connect the component to Redux
export default connect(mapStateToProps, mapDispatchToProps)(Layout)
